import { useCallback, useEffect, useState } from 'react';
import type { DeviceDetail } from '@mantelpiece/shared';
import { api, type StoredSession } from '../lib/api';
import { go } from '../lib/route';
import { Shell } from '../components/Shell';
import { AlbumsTab } from '../components/AlbumsTab';
import { FramePicker } from '../components/FramePicker';
import { PeopleTab } from '../components/PeopleTab';
import { PhotosTab } from '../components/PhotosTab';
import { ScreensTab } from '../components/ScreensTab';
import { SettingsTab } from '../components/SettingsTab';
import { SharingTab } from '../components/SharingTab';
import { UploadPanel } from '../components/UploadPanel';
import { StandingNotice } from '../components/BillingBanner';

export type Guard = (task: () => Promise<void>) => Promise<void>;

interface Props {
  readonly session: StoredSession;
  readonly deviceId: string;
  readonly tab: string;
  readonly onSignOut: () => void;
}

export const Device = ({ session, deviceId, tab, onSignOut }: Props) => {
  const [device, setDevice] = useState<DeviceDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  /** Extra frames this batch should also land on, beyond the one being viewed. */
  const [alsoTo, setAlsoTo] = useState<readonly string[]>([]);

  const guard: Guard = useCallback(async (task) => {
    setError(null);
    try {
      await task();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That did not work.');
    }
  }, []);

  const reload = useCallback(async () => {
    try {
      setDevice(await api.getDevice(session.token, deviceId));
    } catch (caught) {
      setMissing(true);
      setError(caught instanceof Error ? caught.message : 'That frame is not available.');
    }
  }, [deviceId, session.token]);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (missing) {
    return (
      <Shell who={session.account.name} onSignOut={onSignOut} back={{ label: 'All frames', to: '/' }}>
        <p className="alert" style={{ marginTop: '2rem' }}>
          {error ?? 'That frame is not available.'}
        </p>
      </Shell>
    );
  }

  if (!device) {
    return (
      <Shell who={session.account.name} onSignOut={onSignOut}>
        <p className="eyebrow" style={{ marginTop: '2rem' }}>
          Loading
        </p>
      </Shell>
    );
  }

  const { permissions } = device;

  // Tabs are built from what this person may actually do, so a guest sees two
  // of them and an owner sees seven. Nothing is shown-but-disabled.
  const tabs: readonly { id: string; label: string; badge?: number }[] = [
    { id: 'photos', label: 'Photos' },
    ...(permissions.canManage && device.pendingCount > 0
      ? [{ id: 'waiting', label: 'Waiting', badge: device.pendingCount }]
      : permissions.canManage
        ? [{ id: 'waiting', label: 'Waiting' }]
        : []),
    ...(permissions.canSend ? [{ id: 'add', label: 'Add photos' }] : []),
    ...(permissions.canManage
      ? [
          { id: 'albums', label: 'Albums' },
          { id: 'people', label: 'People' },
          { id: 'sharing', label: 'Sharing' },
          { id: 'screens', label: 'Screens' },
          { id: 'slideshow', label: 'Slideshow' },
        ]
      : []),
  ];

  const active = tabs.some((entry) => entry.id === tab) ? tab : 'photos';

  return (
    <Shell
      title={device.label}
      who={session.account.name}
      onSignOut={onSignOut}
      back={{ label: 'All frames', to: '/' }}
    >
      <div className="tabs" role="tablist">
        {tabs.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            className="tab"
            aria-selected={active === entry.id}
            onClick={() => go(`/d/${device.id}/${entry.id}`)}
          >
            {entry.label}
            {entry.badge ? <span className="pill" data-tone="wait">{entry.badge}</span> : null}
          </button>
        ))}
      </div>

      {error && <p className="alert">{error}</p>}

      {device.standing !== 'live' && (
        <StandingNotice standing={device.standing} role={device.role} ownerName={device.ownerName} />
      )}

      {active === 'photos' && (
        <PhotosTab session={session} device={device} guard={guard} status="approved" />
      )}
      {active === 'waiting' && (
        <PhotosTab
          session={session}
          device={device}
          guard={guard}
          status="pending"
          onReviewed={reload}
        />
      )}
      {active === 'add' && (
        <section className="stack-loose">
          <div className="section-head">
            <p className="eyebrow">Add photos</p>
          </div>
          {!permissions.autoAccept && (
            <p className="alert" data-tone="ok">
              Photos you send here wait for {device.ownerName} to approve them before they reach
              the TV.
            </p>
          )}

          {/* One batch, several frames. The file is resized and uploaded once
              whichever way this goes — the extra frames are extra rows. */}
          <div className="stack">
            <p className="eyebrow">Also send to</p>
            <FramePicker
              session={session}
              selected={alsoTo}
              onChange={setAlsoTo}
              exclude={[device.id]}
              emptyText="No other frames you can send to."
            />
          </div>

          <UploadPanel
            headline={
              alsoTo.length > 0
                ? `Choose photos for ${alsoTo.length + 1} frames`
                : 'Choose photos'
            }
            upload={async (photo, text) => {
              if (alsoTo.length === 0) {
                return api.uploadPhoto(session.token, device.id, photo, text);
              }
              const result = await api.uploadToFrames(
                session.token,
                [device.id, ...alsoTo],
                photo,
                text,
              );
              // The panel reports one outcome, so report this frame's — it is
              // the one being looked at. The others are named in the picker.
              const here = result.results.find((entry) => entry.deviceId === device.id);
              return {
                id: here?.id ?? result.assetId,
                duplicate: here?.duplicate ?? false,
                accepted: here?.accepted ?? result.results.some((entry) => entry.accepted),
              };
            }}
          />
        </section>
      )}
      {active === 'albums' && <AlbumsTab session={session} device={device} guard={guard} />}
      {active === 'people' && <PeopleTab session={session} device={device} guard={guard} />}
      {active === 'sharing' && (
        <SharingTab session={session} device={device} guard={guard} onChanged={reload} />
      )}
      {active === 'screens' && <ScreensTab session={session} device={device} guard={guard} />}
      {active === 'slideshow' && (
        <SettingsTab session={session} device={device} guard={guard} onRenamed={reload} />
      )}

      {/* A guest has no Slideshow tab to put this on, and needing to ask the
          owner to un-share something with you is a bad way to leave a room. */}
      {!permissions.canManage && device.role !== 'owner' && (
        <p className="note" style={{ marginTop: '3rem' }}>
          <button
            type="button"
            className="linky"
            onClick={() =>
              void guard(async () => {
                await api.leaveDevice(session.token, device.id);
                go('/');
              })
            }
          >
            Leave this frame
          </button>
        </p>
      )}
    </Shell>
  );
};
